// src/components/Integrations/TwoN/TwoNConfigDialog.jsx
// Configuration dialog for 2N Access Commander integration

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  AlertTitle,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { twonApi } from '../../../services/twonService';

const TwoNConfigDialog = ({ open, onClose, onSaved, commanderConfig = null }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [formData, setFormData] = useState({
    commanderName: '',  // Phase 2: Added commander name field
    commanderUrl: '',
    bearerToken: '',  // Changed from username/password to bearer token
    verifySsl: true,
    syncEnabled: false,
    syncInterval: 15,
    autoLinkLocations: true,
  });

  // UI state
  const [showToken, setShowToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [errors, setErrors] = useState({});

  // Load current configuration when dialog opens
  useEffect(() => {
    if (open && commanderConfig) {
      setFormData({
        commanderName: commanderConfig.commander_name || '',
        commanderUrl: commanderConfig.commander_url || '',
        bearerToken: '', // Never pre-fill token for security
        verifySsl: commanderConfig.verify_ssl !== false,
        syncEnabled: commanderConfig.sync_enabled || false,
        syncInterval: commanderConfig.sync_interval_minutes || 15,
        autoLinkLocations: commanderConfig.auto_link_locations !== false,
      });
    } else if (open && !commanderConfig) {
      // Reset to defaults for new configuration
      setFormData({
        commanderName: '',
        commanderUrl: '',
        bearerToken: '',
        verifySsl: true,
        syncEnabled: false,
        syncInterval: 15,
        autoLinkLocations: true,
      });
    }
    setTestResult(null);
    setErrors({});
  }, [open, commanderConfig]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.commanderName.trim()) {
      newErrors.commanderName = 'Commander name is required';
    }

    if (!formData.commanderUrl.trim()) {
      newErrors.commanderUrl = 'Commander URL is required';
    } else if (!formData.commanderUrl.match(/^https?:\/\/.+/)) {
      newErrors.commanderUrl = 'Please enter a valid URL (http:// or https://)';
    }

    if (!formData.bearerToken.trim() && !commanderConfig) {
      // Only require token for new configs or if updating
      newErrors.bearerToken = 'Bearer token is required';
    }

    if (formData.syncInterval < 1 || formData.syncInterval > 1440) {
      newErrors.syncInterval = 'Sync interval must be between 1 and 1440 minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors before testing', { variant: 'warning' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const testData = {
        commander_url: formData.commanderUrl,
        bearer_token: formData.bearerToken,
        verify_ssl: formData.verifySsl,
      };

      const response = await twonApi.testConnection(testData);

      if (response.data.success) {
        setTestResult({
          success: true,
          message: 'Connection successful! 2N Commander is reachable.',
          details: response.data.details,
        });
        enqueueSnackbar('Connection test successful', { variant: 'success' });
      } else {
        throw new Error(response.data.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Connection test failed',
      });
      enqueueSnackbar('Connection test failed', { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors', { variant: 'warning' });
      return;
    }

    setSaving(true);

    try {
      const saveData = {
        commander_name: formData.commanderName,  // Phase 2: Added
        commander_url: formData.commanderUrl,
        bearer_token: formData.bearerToken,
        verify_ssl: formData.verifySsl,
        sync_enabled: formData.syncEnabled,  // Phase 2: Added
        sync_interval_minutes: formData.syncInterval,
        auto_link_locations: formData.autoLinkLocations,
      };

      let response;
      if (commanderConfig?.id) {
        response = await twonApi.updateConfig(commanderConfig.id, saveData);
      } else {
        response = await twonApi.saveConfig(saveData);
      }

      if (response.data.success) {
        enqueueSnackbar('Configuration saved successfully', { variant: 'success' });
        if (onSaved) {
          onSaved(response.data.config || response.data.data);
        }
        onClose();
      } else {
        throw new Error(response.data.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Save error:', error);
      enqueueSnackbar(
        error.response?.data?.error || error.message || 'Failed to save configuration',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
          {commanderConfig ? 'Edit' : 'Configure'} 2N Access Commander
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure connection to your 2N Access Commander instance for automated device
          synchronization and remote management.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Commander Name - Phase 2 */}
          <TextField
            label="Commander Name"
            value={formData.commanderName}
            onChange={handleChange('commanderName')}
            error={Boolean(errors.commanderName)}
            helperText={errors.commanderName || 'A unique name to identify this Commander (e.g., Headquarters, Building A)'}
            fullWidth
            required
            placeholder="e.g., Headquarters"
          />

          {/* Commander URL */}
          <TextField
            label="Commander URL"
            value={formData.commanderUrl}
            onChange={handleChange('commanderUrl')}
            error={Boolean(errors.commanderUrl)}
            helperText={errors.commanderUrl || 'e.g., https://commander.example.com'}
            fullWidth
            required
            placeholder="https://commander.example.com"
          />

          {/* Bearer Token */}
          <TextField
            label="Bearer Token"
            type={showToken ? 'text' : 'password'}
            value={formData.bearerToken}
            onChange={handleChange('bearerToken')}
            error={Boolean(errors.bearerToken)}
            helperText={
              errors.bearerToken ||
              'Get this from your 2N Commander API settings. Format: dCY8/6o2ZE+G01f6UMsHYL...'
            }
            fullWidth
            required
            placeholder="Paste your bearer token here"
            InputProps={{
              style: { fontFamily: 'monospace' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowToken(!showToken)}
                    edge="end"
                    size="small"
                  >
                    {showToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Divider sx={{ my: 1 }} />

          {/* SSL Verification */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.verifySsl}
                onChange={handleChange('verifySsl')}
              />
            }
            label="Verify SSL certificate (recommended)"
          />

          {/* Enable Automatic Sync - Phase 2 */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.syncEnabled}
                onChange={handleChange('syncEnabled')}
              />
            }
            label="Enable automatic synchronization"
          />

          {/* Sync Interval */}
          <TextField
            label="Sync Interval (minutes)"
            type="number"
            value={formData.syncInterval}
            onChange={handleChange('syncInterval')}
            error={Boolean(errors.syncInterval)}
            helperText={errors.syncInterval || 'How often to sync device status (default: 15 minutes)'}
            fullWidth
            inputProps={{ min: 1, max: 1440 }}
          />

          {/* Auto-link Locations */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.autoLinkLocations}
                onChange={handleChange('autoLinkLocations')}
              />
            }
            label="Automatically link devices to locations based on device names"
          />

          <Divider sx={{ my: 1 }} />

          {/* Test Connection Button */}
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={20} /> : null}
            fullWidth
          >
            {testing ? 'Testing Connection...' : 'Test Connection'}
          </Button>

          {/* Test Result */}
          {testResult && (
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <SuccessIcon /> : <ErrorIcon />}
            >
              <AlertTitle>{testResult.success ? 'Success' : 'Error'}</AlertTitle>
              {testResult.message}
              {testResult.details && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {testResult.details}
                </Typography>
              )}
            </Alert>
          )}

          {/* Info Alert */}
          <Alert severity="info">
            <AlertTitle>Connection Requirements</AlertTitle>
            <Typography variant="body2">
              • Ensure your 2N Access Commander is accessible from this network
              <br />
              • API access must be enabled in Commander settings
              <br />
              • Bearer token must be generated from 2N Commander API settings
              <br />
              • Token must have device read/write permissions
              <br />• For self-signed certificates, disable SSL verification (not recommended for
              production)
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoNConfigDialog;
